import React from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { Switch } from '@repo/ui'

import { FieldLabel } from './FieldLabel'

export interface ToggleRowProps {
  /** The setting's name, in the same treatment every other field label takes. */
  label: React.ReactNode
  /**
   * One sentence saying what turning it on actually does — who sees what, and
   * for how long. Optional, but a toggle whose label does not fully state its
   * consequence should have one rather than leaving the reader to guess.
   */
  description?: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

/**
 * One setting: a label, an optional sentence under it, and a switch on the right.
 *
 * ## Extracted, not invented (§36)
 *
 * `GiftCertificate` hand-rolled this twice — "Send anonymously" and "Show my name in this
 * item's history" — and the second one's own comment says so outright: *"Markup matches
 * the 'Send anonymously' row above verbatim."* A convention recorded in a comment is a
 * copy waiting to drift, and the sell sheet was about to become the third instance
 * (VERA-632 review, converting its "Show ownership" checkbox to a toggle). Three
 * copies of one row is the threshold §36 names, so the row is a component and all three
 * call sites render it.
 *
 * ## What it deliberately does NOT own
 *
 * **Its outer gap.** These rows live in a sheet's one `Stack spacing={3}`, which owns
 * every gap between fields; a margin here would double-count it (MUI's Stack spacing rule
 * also silently kills a local `mb` on a direct child, so the bug would be invisible in the
 * source and visible only on screen). The caller wraps it in whatever its layout needs.
 *
 * **The label typography.** `FieldLabel` is the one source for that (§31), which is what
 * keeps a toggle's name reading identically to a text field's two rows above it.
 *
 * ## Not `PreferenceRow`
 *
 * That component is a GRID — N channel columns per row, with a shared header aligning
 * them — and its whole design is about keeping several switches in a straight vertical
 * line across many rows. A single setting in a form is not that shape, and passing one
 * through a `switches: (PreferenceSwitch | null)[]` array to get one control would be
 * borrowing the machinery and none of the reason for it.
 */
export const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}) => (
  /*
    `center`, against the full label+description block rather than the label line alone.
    VERA-304 round 4 settled this on the gift sheet by explicit feedback, and it is
    reproduced rather than re-litigated: aligning the switch to just the first line reads
    as the control belonging to the label instead of to the setting.
  */
  <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
    <Box>
      <FieldLabel label={label} />
      {description && (
        /* 14px secondary — the helper floor. `body2` IS 14/1.45 in this theme, so the
           variant states it rather than a `caption` overridden back up to 14. */
        <Typography variant='body2' color='text.secondary'>
          {description}
        </Typography>
      )}
    </Box>
    {/*
      `flexShrink: 0` so a long label wraps instead of squeezing the control. The switch is
      42px and is the row's only interactive element; a compressed one is a smaller tap
      target for the sake of text that had somewhere else to go.
    */}
    <Box sx={{ flexShrink: 0 }}>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
      />
    </Box>
  </Stack>
)
